<script setup lang="ts">
import Check from 'lucide-vue-next/dist/esm/icons/check.js';

const props = withDefaults(
  defineProps<{
    selected?: boolean;
    danger?: boolean;
    disabled?: boolean;
  }>(),
  { selected: false, danger: false, disabled: false }
);

const emit = defineEmits<{ select: [] }>();

function onClick(): void {
  if (props.disabled) return;
  emit('select');
}
</script>

<template>
  <button
    type="button"
    class="nx-menu-item"
    :class="{ selected, danger, disabled }"
    :disabled="disabled"
    @click="onClick"
  >
    <slot />
    <!-- Trailing, so the labels of all rows stay aligned. -->
    <Check v-if="selected" class="nx-menu-item__check" aria-hidden="true" />
  </button>
</template>

<style scoped>
.nx-menu-item {
  padding: calc(var(--nxt-space-xs) * 1.25) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  font-size: var(--nxt-menu-item-size);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-menu-item-fg);
  background: transparent;
  border: none;
  cursor: pointer;

  display: flex;
  align-items: center;
  text-align: left;
  white-space: nowrap;
  width: 100%;
  gap: var(--nxt-space-xs);
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-menu-item:hover:not(:disabled) {
  background-color: var(--nxt-menu-item-bg-hover);
  color: var(--nxt-menu-item-fg);
}

.nx-menu-item:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

/* A brand tint that reads the same in both themes (the subtle token is near
   black in dark mode); the check carries the brand colour. */
.nx-menu-item.selected {
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-menu-item.selected:hover:not(:disabled) {
  background: color-mix(in srgb, var(--nxt-brand-bg) 18%, transparent);
  color: var(--nxt-fg);
}

.nx-menu-item__check {
  flex: none;
  width: var(--nxt-icon-size-sm);
  height: var(--nxt-icon-size-sm);
  margin-inline-start: auto;
  padding-inline-start: var(--nxt-space-sm);
  box-sizing: content-box;
  color: var(--nxt-brand-bg);
}

.nx-menu-item.danger {
  color: var(--nxt-danger-bg);
}

.nx-menu-item.danger:hover:not(:disabled) {
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
}

.nx-menu-item:disabled,
.nx-menu-item.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .nx-menu-item {
    transition: none;
  }
}
</style>
